import StorageService from '@/services/storage.service'
import storageKeys from '@/constants/storageKeys'
import { defaultFilters } from '@/models/OfferPartners/helpers'
import offerPartnersRepository from '@/repository/generatedRepository/offerPartnersRepository'
import TableOptions from '@/models/TableOptions'
import { omitEmptyDeepFilters, prepareFilters } from '@/helpers/filters'
import partnersEventRepository from "@/repository/generatedRepository/partnersEventRepository";
import partnerStreamRepository from "@/repository/generatedRepository/partnerStreamRepository";
import PartnerGroupRepository from "@/repository/generatedRepository/partnerGroupRepository";
import offerPartnerLandingRepository from "@/repository/generatedRepository/offerPartnerLandingRepository";
import offerPartnerCostHistoryRepository from "@/repository/generatedRepository/offerPartnerCostHistoryRepository";
import registrationDaysHistoryRepository
  from "@/repository/generatedRepository/offerPartnerRegistrationDaysHistoryRepository";
import baselineDepositHistoryRepository
  from "@/repository/generatedRepository/BaselineDepositHistoryRepository";
import baselinePeriodHistoryRepository
  from "@/repository/generatedRepository/BaselinePeriodHistoryRepository";
import baselineCountryRequirementsRepository
  from "@/repository/generatedRepository/BaselineCountryRequirementsRepository";
import moment from 'moment'
import i18n from '@/plugins/vue-i18n'
import { DATE_FORM_FORMAT } from '@/constants/date'
const { saveAs } = require('file-saver')

// "Select all (N)" of the bulk actions: backend picks offer partners by the current list filters
const bulkSelectionQuery = (filters, { all }) =>
  all
    ? {
        filter: omitEmptyDeepFilters(filters.filter),
        cfFilter: omitEmptyDeepFilters(filters.cfFilter),
      }
    : {}

const state = {
  list: [],
  filters: StorageService.get(storageKeys.OFFER_PARTNERS_FILTERS) || { ...defaultFilters() },
  tableOptions: TableOptions.defaultTableOptions(),
}

const getters = {
  filters: state => state.filters,
  tableOptions: state => state.tableOptions,
  list: state => state.list,
  isOpenFilterPanel: state =>
    state.filters.clientOptions && state.filters.clientOptions.filterPanelExpanded,
}

const actions = {
  async loadData(context, payload) {
    const { signal, ...filters } = payload
    context.commit('setFilters', filters)
    StorageService.set(storageKeys.OFFER_PARTNERS_FILTERS, filters)

    const appliedFilters = omitEmptyDeepFilters(filters.filter)
    const appliedCfFilters = omitEmptyDeepFilters(filters.cfFilter)

    const preparedFilters = prepareFilters({
      ...filters,
      filter: appliedFilters,
      cfFilter: appliedCfFilters,
      signal,
    })

    const { data } = await offerPartnersRepository.list(preparedFilters)

    const idsStr = filters.filter?.landings?.landing?.id?.value
    if (idsStr) {
      const ids = new Set(String(idsStr).split('|').map(Number).filter(Boolean))
      data.items.forEach(item => {
        item.landings = item.landings?.filter(l => ids.has(l.landing?.id)) ?? []
      })
    }

    context.commit('setList', { data, filters })

    return { products: data.items, pagination: data.pagination }
  },
  async searchOfferPartners(context, filters) {
    const appliedFilters = omitEmptyDeepFilters(filters.filter)
    const preparedFilters = prepareFilters({
      ...filters,
      filter: appliedFilters,
    })
    const { data } = await offerPartnersRepository.list(preparedFilters)
    return { products: data.items, pagination: data.pagination }
  },
  async loadExportFile(context, payload) {
    const { signal, ...filters } = payload || {}
    const appliedFilters = omitEmptyDeepFilters(filters.filter)
    const appliedCfFilters = omitEmptyDeepFilters(filters.cfFilter)

    const preparedFilters = prepareFilters({
      ...filters,
      filter: appliedFilters,
      cfFilter: appliedCfFilters,
      signal,
    })

    const response = await offerPartnersRepository.exportOfferPartners(
      { ...preparedFilters },
      { responseType: 'blob' },
    )
    const blob = new Blob([response.data], { type: response.headers['content-type'] })
    const filename = `${i18n.tc('offerPartners.listTitle')} ${moment().format(DATE_FORM_FORMAT)}.csv`
    saveAs(blob, filename)
    return response
  },
  async loadItem(context, id) {
    const { data } = await offerPartnersRepository.get(id)
    return data
  },
  createOfferPartner(context, payload) {
    return offerPartnersRepository.post(payload)
  },
  updateOfferPartner(context, payload) {
    const { id, ...data } = payload
    return offerPartnersRepository.put(id, data)
  },
  deleteOfferPartner(context, payload) {
    return offerPartnersRepository.delete(payload)
  },
  filtersStorageReset() {
    StorageService.remove(storageKeys.OFFER_PARTNERS_FILTERS)
  },
  updateFiltersClientOptions({ commit, getters }, clientOptions) {
    const updatedFilters = {
      ...getters.filters,
      clientOptions,
    }
    commit('setFilters', updatedFilters)
    StorageService.set(storageKeys.OFFER_PARTNERS_FILTERS, updatedFilters)
  },
  async loadPartnerEvent(context, filters) {
    const { data } = await partnersEventRepository.list(filters)
    return data
  },
  createPartnerEvent(context, payload) {
    return partnersEventRepository.createEventGroup(payload)
  },
  updatePartnerEvent(context, payload) {
    return partnersEventRepository.updateEventGroup(payload)
  },
  deletePartnerEvent(context, payload) {
    return partnersEventRepository.delete(payload)
  },
  async loadPartnerStream(context, filters) {
    const { data } = await partnerStreamRepository.list(filters)
    return data
  },
  async searchPartnerStream(context, filters) {
    const appliedFilters = omitEmptyDeepFilters(filters.filter)
    const preparedFilters = prepareFilters({
      ...filters,
      filter: appliedFilters,
    })

    const { data } = await partnerStreamRepository.list(preparedFilters)

    return { products: data.items, pagination: data.pagination }
  },
  createPartnerStream(context, payload) {
    return partnerStreamRepository.post(payload)
  },
  updatePartnerStream(context, payload) {
    return partnerStreamRepository.updatePartnerStream(payload)
  },
  deletePartnerStream(context, payload) {
    return partnerStreamRepository.delete(payload)
  },
  validateOffer(context, payload) {
    return partnerStreamRepository.validateOffer(payload)
  },
  validateTrafficSource(context, payload) {
    return partnerStreamRepository.validateTrafficSource(payload)
  },
  async searchPartnerPartnerGroups(context, filters) {
    const { data } = await PartnerGroupRepository.list(filters)
    return data
  },
  createPartnerGroup(context, payload) {
    return PartnerGroupRepository.post(payload)
  },
  updatePartnerGroup(context, payload) {
    return PartnerGroupRepository.updatePartnerGroup(payload)
  },
  parsePartnerGroup(context, payload) {
    return PartnerGroupRepository.updatePartnerGroupManual(payload)
  },
  parsePartnerGroupApi(context, payload) {
    return PartnerGroupRepository.updatePartnerGroupApi(payload)
  },
  deletePartnerGroup(context, payload) {
    return PartnerGroupRepository.delete(payload)
  },
  createOfferPartnerLanding(context, payload) {
    return offerPartnerLandingRepository.post(payload)
  },
  updateOfferPartnerLanding(context, payload) {
    return offerPartnerLandingRepository.updateLanding(payload)
  },
  deleteOfferPartnerLanding(context, payload) {
    return offerPartnerLandingRepository.delete(payload)
  },
  createCostHistory(context, payload) {
    return offerPartnerCostHistoryRepository.post(payload)
  },
  updateCostHistory(context, { id, ...payload }) {
    return offerPartnerCostHistoryRepository.put(id, payload)
  },
  deleteCostHistory(context, payload) {
    return offerPartnerCostHistoryRepository.delete(payload)
  },
  createRegistrationDaysHistory(context, payload) {
    return registrationDaysHistoryRepository.post(payload)
  },
  updateRegistrationDaysHistory(context, { id, ...payload }) {
    return registrationDaysHistoryRepository.put(id, payload)
  },
  deleteRegistrationDaysHistory(context, payload) {
    return registrationDaysHistoryRepository.delete(payload)
  },
  createBaselineDepositHistory(context, payload) {
    return baselineDepositHistoryRepository.post(payload)
  },
  updateBaselineDepositHistory(context, { id, ...payload }) {
    return baselineDepositHistoryRepository.put(id, payload)
  },
  deleteBaselineDepositHistory(context, payload) {
    return baselineDepositHistoryRepository.delete(payload)
  },
  createBaselinePeriodHistory(context, payload) {
    return baselinePeriodHistoryRepository.post(payload)
  },
  updateBaselinePeriodHistory(context, { id, ...payload }) {
    return baselinePeriodHistoryRepository.put(id, payload)
  },
  deleteBaselinePeriodHistory(context, payload) {
    return baselinePeriodHistoryRepository.delete(payload)
  },
  createCountryRequirement(context, payload) {
    return baselineCountryRequirementsRepository.post(payload)
  },
  updateCountryRequirements(context, { id, ...payload }) {
    return baselineCountryRequirementsRepository.put(id, payload)
  },
  deleteCountryRequirements(context, payload) {
    return baselineCountryRequirementsRepository.delete(payload)
  },
  bulkSaveOfferPartnerHistories(context, { id, payload }) {
    return offerPartnersRepository.bulkSave(id, payload)
  },
  async bulkCancelCpaCosts({ state }, payload) {
    const { data } = await offerPartnersRepository.bulkCancelCpaCosts(payload, bulkSelectionQuery(state.filters, payload))
    return data
  },
  async bulkUpdateCpaSettings({ state }, payload) {
    const { data } = await offerPartnersRepository.bulkUpdateCpaSettings(payload, bulkSelectionQuery(state.filters, payload))
    return data
  },
  async checkOfferPartnerTraffic(context, id) {
    const { data } = await offerPartnersRepository.checkTraffic(id)
    return data
  },
  resetSearchFilter({ commit }) {
    commit('clearSearch')
  },
}

const mutations = {
  setList(state, { data, filters }) {
    state.list = [...data.items]
    state.tableOptions = new TableOptions(data.pagination, filters)
  },
  setFilters(state, filters) {
    state.filters = { ...filters }
  },
  clearSearch(state) {
    state.tableOptions.search = ''
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
